import { useEffect, useRef, useState } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValueEvent,
} from "framer-motion";
import { SEQUENCE_FRAMES } from "../data/site";
import Hero from "./Hero";
import Expertise from "./Expertise";

const Frame = ({ frame, index, total, progress }) => {
  const w = 1 / total;
  const start = index * w;
  const fade = 0.5 * w;
  const opacity = useTransform(
    progress,
    index === 0 ? [0, 1] : [Math.max(0, start - fade), start],
    index === 0 ? [1, 1] : [0, 1]
  );
  const win =
    index === 0
      ? [0, w]
      : [Math.max(0, start - fade), Math.min(1, start + w)];
  const scale = useTransform(progress, win, [1.02, 1.14]);
  const x = useTransform(
    progress,
    win,
    index % 2 === 0 ? ["-1.5%", "1.5%"] : ["1.5%", "-1.5%"]
  );

  return (
    <motion.div style={{ opacity }} className="absolute inset-0">
      <motion.img
        src={frame.url}
        alt={frame.label}
        style={{ scale, x }}
        loading={index < 2 ? "eager" : "lazy"}
        className="h-full w-full object-cover will-change-transform"
      />
      <div className="absolute inset-0 bg-navy-950/45" />
    </motion.div>
  );
};

const Quote = ({ progress }) => {
  const opacity = useTransform(progress, [0.86, 0.93], [0, 1]);
  const y = useTransform(progress, [0.86, 0.97], ["12%", "0%"]);
  return (
    <motion.div
      style={{ opacity, y }}
      className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center px-6"
      data-testid="sequence-quote"
    >
      <div className="text-center">
        <div className="mx-auto mb-8 h-px w-24 bg-gold/70" />
        <p className="font-serif text-2xl font-medium leading-snug text-white sm:text-3xl lg:text-4xl">
          Fifteen years.
          <br />
          One uncompromising standard.
        </p>
        <div className="mx-auto mt-8 h-px w-24 bg-gold/70" />
      </div>
    </motion.div>
  );
};

export default function ScrollSequence() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });
  const total = SEQUENCE_FRAMES.length;
  const [current, setCurrent] = useState(0);

  useMotionValueEvent(scrollYProgress, "change", (v) => {
    const i = Math.min(total - 1, Math.max(0, Math.floor(v * total)));
    setCurrent((prev) => (prev === i ? prev : i));
  });

  useEffect(() => {
    SEQUENCE_FRAMES.forEach((f) => {
      const img = new Image();
      img.src = f.url;
    });
  }, []);

  return (
    <section
      ref={ref}
      id="cinematic"
      data-testid="cinematic-sequence"
      style={{ height: `${total * 110}vh` }}
      className="relative"
    >
      <div className="sticky top-0 h-screen overflow-hidden bg-navy-950">
        {SEQUENCE_FRAMES.map((frame, i) => (
          <Frame
            key={frame.url}
            frame={frame}
            index={i}
            total={total}
            progress={scrollYProgress}
          />
        ))}

        <div className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(ellipse_at_center,transparent_45%,rgba(3,10,20,0.55)_100%)]" />

        <Hero progress={scrollYProgress} />
        <Expertise progress={scrollYProgress} />
        <Quote progress={scrollYProgress} />

        <div
          className="absolute bottom-8 right-6 z-30 flex items-center gap-4 md:right-10"
          data-testid="sequence-frame-indicator"
        >
          <span className="text-[0.6rem] font-light tracking-[0.3em] text-slate-400">
            {SEQUENCE_FRAMES[current].label}
          </span>
          <span className="font-serif text-xs tracking-[0.2em] text-gold">
            {String(current + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </span>
        </div>
      </div>
    </section>
  );
}
